import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangleIcon, LockIcon } from 'lucide-react';
import { dayLong, today } from '../../core/format/dates';
import { salonDayFor } from '../../core/rules/salonDays';
import { Panel } from '../ui/Panel';
import { NumberInput } from '../ui/NumberInput';
import { AutoTextarea } from '../ui/AutoTextarea';
import { useSubmit } from '../../hooks/useSubmit';
import { closeKassaDay } from '../adminApi';
import { newRequestId } from '../../core/api/http';
import { CLOSE_WORD, closeProblem, closeWarnings, isCloseWord } from '../../core/rules/kassa';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { money, toCents } from '../../core/format/currency';
import { clock, refundMethodLabel } from './kassaLabels';
import { printKuurCreditNote } from './kuurCreditNote';
import { printReturnCreditNote } from './returnCreditNote';
import { useAdmin } from '../AdminContext';
import { closedByLabel } from '../../core/rules/staffAccess';
import type { KassaDay, KuurStop, SaleReturn } from '../../core/types/kassa';
type Props = {
  data: KassaDay;
  onClosed: (day: KassaDay) => void;
  /** Collega: geen dagtotalen en blind tellen (core/rules/staffAccess). */
  totalsHidden?: boolean;
};

/** Het dagoverzicht: wat er binnenkwam per methode, en de afsluiting met de telling van de kas. */
export function DaySummary({
  data,
  onClosed,
  totalsHidden = false
}: Props) {
  const s = data.summary;
  const canClose = !data.closed;
  const count = s.sales === 0 ? 'Nog geen bonnen.' : `${s.sales} ${s.sales === 1 ? 'bon' : 'bonnen'}${s.corrected ? ` · ${s.corrected} gecorrigeerd` : ''}`;
  if (totalsHidden) {
    return <Panel title="Dag afsluiten" description={`Jouw bonnen vandaag: ${count.toLowerCase()}`} data-id="element-2332" data-mp="3jmzg1cvzf2ip">
        {data.closed ? <p className="flex items-center gap-2 text-[13px] text-ink" data-id="element-2333" data-mp="12xkekxleh9ew">
            <LockIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2334" data-mp="ywqru7jp20nl" />
            Afgesloten{closedByLabel(data.closed)} om {clock(data.closed.closedAt)} · geteld {money(data.closed.countedCashCents)}
          </p> : canClose && <CloseForm data={data} onClosed={onClosed} blind data-id="element-2335" data-mp="j31pbu8lo9v" />}
      </Panel>;
  }
  return <Panel title="Dagoverzicht" description={count} data-id="element-2336" data-mp="5kscqrhgm1xy">
      <p className="font-display text-[2.2rem] font-light leading-none tabular-nums text-ink" data-id="element-2337" data-mp="9lvu5if5o61a">{money(s.totalCents)}</p>
      <p className="mt-1 text-[12px] text-muted" data-id="element-2338" data-mp="3920u7a89yzs5">Totaal incl. btw</p>

      <dl className="mt-5 space-y-1.5 border-t border-line pt-4 text-[14px]" data-id="element-2339" data-mp="3dzartd5tihr2">
        <Row label="Pin" value={s.byMethod.pin} data-id="element-2340" data-mp="3i1fjw7gmb6bl" />
        <Row label="Contant" value={s.byMethod.contant} data-id="element-2341" data-mp="3i0m9mwig9yop" />
        <Row label="Cadeaubon" value={s.byMethod.cadeaubon} data-id="element-2342" data-mp="3mrvwxqgj0pxf" />
        {(s.byMethod.tegoed ?? 0) > 0 && <Row label="Klanttegoed" value={s.byMethod.tegoed} data-id="element-2343" data-mp="2qa80fdldtajd" />}
      </dl>
      {s.voidRefunds && s.voidRefunds.pin + s.voidRefunds.contant > 0 && <p className="mt-3 text-[12px] text-muted" data-id="element-2344" data-mp="2uaig5hyz7fbf">
          Teruggegeven bij gecorrigeerde bonnen:{' '}
          {[s.voidRefunds.pin > 0 ? `${money(s.voidRefunds.pin)} pin` : '', s.voidRefunds.contant > 0 ? `${money(s.voidRefunds.contant)} contant` : ''].filter(Boolean).join(' · ')}. Die bonnen
          tellen hierboven al niet mee; op de pinautomaat staat de terugboeking wel.
        </p>}

      {(data.kuurRefunds?.length ?? 0) > 0 && <KuurRefunds refunds={data.kuurRefunds ?? []} data-id="element-2345" data-mp="23tg3umfuj6oz" />}
      {(data.returns?.length ?? 0) > 0 && <Returns returns={data.returns ?? []} data-id="element-2346" data-mp="23vasobu8niyc" />}

      <dl className="mt-4 space-y-1.5 border-t border-line pt-4 text-[13px]" data-id="element-2347" data-mp="1z1ont5ubgjd3">
        <Row label="Behandelingen" value={s.byKind.treatments} small data-id="element-2348" data-mp="1u34ziys92vhk" />
        <Row label="Producten" value={s.byKind.products} small data-id="element-2349" data-mp="1u1ad1g1byvbd" />
        <Row label="Kuren vooruit" value={s.byKind.kuren} small data-id="element-2350" data-mp="1q3mgk8jzouhd" />
        {(s.byKind.giftCards ?? 0) > 0 && <Row label="Cadeaubonnen verkocht (geen omzet)" value={s.byKind.giftCards} small data-id="element-2351" data-mp="1l2f8s9hujvap" />}
        {s.vat.map(v => <Row key={v.vatRate} label={`Waarvan btw ${v.vatRate}%`} value={v.vatCents} small data-id="element-2352" data-mp="1g5yjhbywvaw1" />)}
      </dl>

      <div className="mt-5 border-t border-line pt-4" data-id="element-2353" data-mp="3l581hg29aq8q">
        {data.closed ? <div className="space-y-1 text-[13px]" data-id="element-2354" data-mp="3l7anlp0afz86">
            <p className="flex items-center gap-2 text-ink" data-id="element-2355" data-mp="36h3fo37wpylq">
              <LockIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2356" data-mp="3bfeqoh9xuf5j" />
              Afgesloten{closedByLabel(data.closed)} om {clock(data.closed.closedAt)}
            </p>
            <p className="text-muted" data-id="element-2357" data-mp="2xhzvsx0x1f66">
              Contant verwacht {money(data.closed.expectedCashCents)} · geteld {money(data.closed.countedCashCents)}
              {data.closed.differenceCents !== 0 && ` · ${data.closed.differenceCents > 0 ? 'over' : 'tekort'} ${money(Math.abs(data.closed.differenceCents))}`}
            </p>
            {data.closed.note && <p className="text-ink" data-id="element-2358" data-mp="lre3xz2i2sx9">“{data.closed.note}”</p>}
          </div> : canClose && <CloseForm data={data} onClosed={onClosed} data-id="element-2359" data-mp="qnusgudm39av" />}
      </div>
    </Panel>;
}

/**
 * Kuren die vandaag met pin of contant zijn terugbetaald (vanuit het klantdossier). Ze zitten al
 * verrekend in Pin en Contant hierboven, en dus in wat er in de la hoort.
 */
function KuurRefunds({
  refunds
}: {
  refunds: KuurStop[];
}) {
  const {
    site
  } = useAdmin();
  const print = async (k: KuurStop) => {
    if (!(await printKuurCreditNote(k, site.salon))) toast.error('Creditnota openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div className="mt-4 border-t border-line pt-4" data-id="element-2360" data-mp="2oxl84qlum4en">
      <p className="text-[13px] text-ink" data-id="element-2361" data-mp="33vvpcuwttz85">Kuur terugbetaald</p>
      <p className="text-[12px] text-muted" data-id="element-2362" data-mp="33vdlpd7u4iq7">Zit al in Pin en Contant hierboven.</p>
      <ul className="mt-2 space-y-1.5 text-[13px]" data-id="element-2363" data-mp="2yxu3y3bxhnti">
        {refunds.map(k => <li key={k.id} className="flex items-baseline justify-between gap-3" data-id="element-2364" data-mp="19h2wh3nbvmjw">
            <span className="min-w-0" data-id="element-2365" data-mp="14j1deipwjn8f">
              <span className="block truncate text-ink" data-id="element-2366" data-mp="14k2y7ewpzo3c">{k.customerName}</span>
              <span className="block text-[12px] text-muted" data-id="element-2367" data-mp="1u7ozb5of2y7u">
                {clock(k.at)} · {refundMethodLabel[k.method]} ·{' '}
                <button type="button" onClick={() => void print(k)} className="underline underline-offset-2 hover:text-ink" data-id="element-2368" data-mp="1y8sctlizsi5w">
                  {k.number}
                </button>
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-ink" data-id="element-2369" data-mp="284loz3ssu9n5">− {money(k.refundCents)}</span>
          </li>)}
      </ul>
    </div>;
}

/** Retouren die vandaag met pin of contant zijn terugbetaald (de bon kan van een eerdere dag zijn). */
function Returns({
  returns
}: {
  returns: SaleReturn[];
}) {
  const {
    site
  } = useAdmin();
  const print = async (r: SaleReturn) => {
    if (!(await printReturnCreditNote(r, site.salon))) toast.error('Creditnota openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div className="mt-4 border-t border-line pt-4" data-id="element-2370" data-mp="30w5a4t8yhxrs">
      <p className="text-[13px] text-ink" data-id="element-2371" data-mp="p80gte3e4iis">Retour</p>
      <p className="text-[12px] text-muted" data-id="element-2372" data-mp="p9lbd3ppg1gn">Zit al in Pin en Contant hierboven.</p>
      <ul className="mt-2 space-y-1.5 text-[13px]" data-id="element-2373" data-mp="k7vwfczd33na">
        {returns.map(r => <li key={r.id} className="flex items-baseline justify-between gap-3" data-id="element-2374" data-mp="f9151mnbw6rk">
            <span className="min-w-0" data-id="element-2375" data-mp="aazlz1pwk7g3">
              <span className="block truncate text-ink" data-id="element-2376" data-mp="ac19w6s0oof8">{r.customerName}</span>
              <span className="block text-[12px] text-muted" data-id="element-2377" data-mp="17yrl418itiqb">
                {clock(r.at)} · {refundMethodLabel[r.method]} · bon {r.saleNumber} ·{' '}
                <button type="button" onClick={() => void print(r)} className="underline underline-offset-2 hover:text-ink" data-id="element-2378" data-mp="131hy0s8ltj5n">
                  {r.number}
                </button>
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-ink" data-id="element-2379" data-mp="1qozl4agxbt2a">− {money(r.refundCents)}</span>
          </li>)}
      </ul>
    </div>;
}
function Row({
  label,
  value,
  small = false
}: {
  label: string;
  value: number;
  small?: boolean;
}) {
  return <div className="flex justify-between gap-3" data-id="element-2380" data-mp="u9wpgfv5j5sb">
      <dt className="text-muted" data-id="element-2381" data-mp="z1eqf3x6d6da">{label}</dt>
      <dd className={`tabular-nums ${small ? 'text-muted' : 'text-ink'}`} data-id="element-2382" data-mp="z27x64w4vvvf">{money(value)}</dd>
    </div>;
}

/**
 * Tel de la, vul het in; bij een verschil schrijf je kort op wat er gebeurde. Daarna kunnen er geen
 * bonnen meer bij. `blind` (collega): zonder het verwachte bedrag; de server rekent het verschil en
 * Sarah ziet het in haar dagoverzicht.
 */
function CloseForm({
  data,
  onClosed,
  blind = false
}: Omit<Props, 'totalsHidden'> & {
  blind?: boolean;
}) {
  const id = useId();
  const {
    site
  } = useAdmin();
  const expected = data.summary.expectedCashCents;
  const [counted, setCounted] = useState<number | undefined>(undefined);
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  // Twee stappen: eerst tellen, dan bevestigen (waarschuwingen lezen en "afsluiten" intikken).
  const [confirming, setConfirming] = useState(false);
  const [word, setWord] = useState('');
  const {
    submit,
    submitting,
    error
  } = useSubmit(closeKassaDay);
  const request = useRef(newRequestId());
  const problem = blind ? counted === undefined ? 'Vul in wat er in de la zit.' : null : closeProblem(counted, expected, note);
  const difference = blind || counted === undefined ? null : counted - expected;
  const isToday = data.day === today();
  const warnings = (() => {
    const now = new Date();
    return closeWarnings({
      toSettle: data.toSettle,
      isToday,
      nowMinutes: now.getHours() * 60 + now.getMinutes(),
      closesAt: salonDayFor(site.openingHours, site.salonExceptions, data.day)?.close ?? null
    });
  })();
  const review = () => {
    setTouched(true);
    if (problem || counted === undefined) return;
    setConfirming(true);
  };
  const close = async () => {
    if (problem || counted === undefined || !isCloseWord(word)) return;
    const day = await submit(data.day, {
      requestId: request.current,
      countedCashCents: counted,
      note: note.trim(),
      confirmWord: word.trim(),
      acknowledged: warnings.map(w => w.code)
    });
    if (!day) return;
    toast.success('Dag afgesloten', {
      description: 'Deze dag neemt geen bonnen meer aan.'
    });
    onClosed(day);
  };
  if (confirming && counted !== undefined) {
    return <div className="space-y-3" data-id="element-2383" data-mp="8s6a5by9f3ae">
        <p className="text-[13px] text-ink" data-id="element-2384" data-mp="3tmo7bjo1ri1">
          {isToday ? 'Vandaag' : dayLong(data.day)} definitief afsluiten
        </p>
        <p className="text-[13px] text-muted" data-id="element-2385" data-mp="3v80sp9klds8i">
          Geteld {money(counted)}
          {difference ? ` · ${difference > 0 ? 'over' : 'tekort'} ${money(Math.abs(difference))}` : ''}. Daarna kunnen er geen bonnen of correcties meer bij, en dat
          is niet terug te draaien.
        </p>
        {warnings.length > 0 && <ul role="list" aria-label="Let op" className="space-y-1.5 rounded-md bg-sand px-3 py-2.5 text-[13px] text-ink" data-id="element-2386" data-mp="3h7ztdyjz6sv5">
            {warnings.map(w => <li key={w.code} className="flex gap-2" data-id="element-2387" data-mp="1rrx8kk81a2c2">
                <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-2388" data-mp="1rti2c7mixh8v" />
                {w.text}
              </li>)}
          </ul>}
        <label htmlFor={`${id}-word`} className="block" data-id="element-2389" data-mp="20pzd39qz7d43">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-2390" data-mp="25q3y9d2tww0n">
            Typ <span className="text-ink" data-id="element-2391" data-mp="191633tr62w2y">{CLOSE_WORD}</span> om te bevestigen{warnings.length > 0 ? ' dat je dit gezien hebt' : ''}
          </span>
          <input id={`${id}-word`} type="text" autoComplete="off" autoCapitalize="off" spellCheck={false} value={word} onChange={e => setWord(e.target.value)} className={`${inputCls} max-w-[12rem]`} data-id="element-2392" data-mp="1d311apdswtci" />
        </label>
        {error && <p role="alert" className="text-[12px] text-clay" data-id="element-2393" data-mp="uc01y08rqvsf">
            {error}
          </p>}
        <div className="flex flex-col gap-2 sm:flex-row" data-id="element-2394" data-mp="1xmnpkih0f2ln">
          <button type="button" onClick={() => void close()} disabled={submitting || !isCloseWord(word)} aria-busy={submitting} className={`${btnPrimary} flex-1 disabled:opacity-50`} data-id="element-2395" data-mp="1xl2w6w6flppd">
            {submitting ? 'Afsluiten…' : 'Definitief afsluiten'}
          </button>
          <button type="button" onClick={() => setConfirming(false)} disabled={submitting} className={`${btnSecondary} flex-1`} data-id="element-2396" data-mp="1enrw59cz6s8v">
            Terug
          </button>
        </div>
      </div>;
  }
  return <div className="space-y-3" data-id="element-2397" data-mp="1np9b2oencnvz">
      <p className="text-[13px] text-ink" data-id="element-2398" data-mp="1npj46kuznb5w">
        Dag afsluiten {blind ? <span className="text-muted" data-id="element-2399" data-mp="1ip52rlnw6zgu">· tel wat er contant in de la zit</span> : <span className="text-muted" data-id="element-2400" data-mp="1irzkv6jfo873">· contant verwacht {money(expected)}</span>}
      </p>
      {!isToday && <p className="text-[12px] text-muted" data-id="element-2401" data-mp="2u523s89099uf">
          Je sluit een eerdere dag af. Tel alleen het geld van die dag: trek eraf wat er vandaag al contant binnenkwam.
        </p>}
      <label className="block" data-id="element-2402" data-mp="32o6pvfi0guak">
        <span className="mb-1 block text-[12px] text-muted" data-id="element-2403" data-mp="36nnzml4h0dlj">Geteld in de la (€, zonder wisselgeldvoorraad)</span>
        <NumberInput decimals allowEmpty value={counted === undefined ? undefined : counted / 100} onChange={v => setCounted(v === undefined ? undefined : toCents(v))} className="w-36" data-id="element-2404" data-mp="2sooazdgyjypd" />
      </label>
      {difference !== null && difference !== 0 && <p className="text-[13px] text-clay" aria-live="polite" data-id="element-2405" data-mp="3qbewq1v38bep">
          {difference > 0 ? 'Over' : 'Tekort'}: {money(Math.abs(difference))}
        </p>}
      <label htmlFor={`${id}-note`} className="block" data-id="element-2406" data-mp="3blz7wrf4ag1n">
        <span className="mb-1 block text-[12px] text-muted" data-id="element-2407" data-mp="3gl22fhxh13vd">{blind ? 'Notitie voor Sarah (optioneel)' : `Notitie ${difference ? '(verplicht bij een verschil)' : '(optioneel)'}`}</span>
        <AutoTextarea id={`${id}-note`} value={note} onChange={e => setNote(e.target.value)} minRows={2} maxLength={300} className={inputCls} data-id="element-2408" data-mp="cwidi98915su" />
      </label>
      {(touched && problem || error) && <p role="alert" className="text-[12px] text-clay" data-id="element-2409" data-mp="2geq651qd7z28">
          {error ?? problem}
        </p>}
      <button type="button" onClick={review} className={`${btnPrimary} w-full`} data-id="element-2410" data-mp="2bevg10eezdi8">
        Dag afsluiten
      </button>
    </div>;
}